import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { cn } from '~/lib/cn';
import { LinkList } from '~/modules/link-farm/components/LinkList/LinkList.tsx';
import type {
  InternalLinkType,
  LinkFarmAppCategory,
  LinkFarmCategory,
} from '~/modules/link-farm/types';
import type { ExternalLink, InternalLink } from '~/modules/link-farm/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { PageTemplate } from './components/PageTemplate';
import { useLinkFarmLinks } from './hooks/useLinkFarmLinks';

interface LinkPageProps {
  category: LinkFarmCategory | LinkFarmAppCategory;
  onInternalLinkClick?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    type?: InternalLinkType,
  ) => void;
}

export function LinkPage({ category, onInternalLinkClick }: LinkPageProps) {
  const { t } = useTranslation('wmgone');
  const { flushAnalytics, trackEvent } = useAnalytics('Link Farm');

  useEffect(() => {
    // Make sure to clean up analytics on unmount.
    return () => {
      flushAnalytics();
    };
  }, [flushAnalytics]);

  useEffect(() => {
    trackEvent('linkFarmPageView', { pageName: category });
  }, [trackEvent, category]);

  const title = t(`links.${category}`);

  // Internal links are displayed at the top of the page.
  const internalLinks = useLinkFarmLinks({
    linkType: 'internal',
    category,
  }) as InternalLink[];

  // External links are displayed underneath a separator at the bottom of the page.
  const externalLinks = useLinkFarmLinks({
    linkType: 'external',
    category,
  }) as ExternalLink[];

  return (
    <PageTemplate>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="flex w-full flex-col">
        <p className="py-lg text-headline-sm font-bold">{title}</p>
        {internalLinks.length ? (
          <>
            <LinkList
              data={internalLinks}
              onInternalLinkClick={onInternalLinkClick}
              className={cn(
                'auto-cols-[480px] grid-cols-[repeat(auto-fit,480px)]',
              )}
            />
            <Separator className="my-xl" />
          </>
        ) : null}
        <LinkList data={externalLinks} />
      </div>
    </PageTemplate>
  );
}

LinkPage.displayName = 'LinkPage';
